import { useStore } from 'hooks/useStore';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export const useBulkUpdate = (
  directData?: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[],
  indirectData?: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[]
) => {
  const {
    ConstrainedForecastViewStore: {
      bulkUpdateDirectTodoNDC,
      bulkUpdateDirectCompletedNDC,
      bulkUpdateIndirectTodoNDC,
      bulkUpdateIndirectCompletedNDC,
      bulkUpdateNDCs,
      isForecastBulkUpdating,
      bulkUpdateConstrainedForecast,
    },
  } = useStore();

  const [directConceptname, setDirectconceptname] = useState<string>('');
  const [indirectConceptname, setIndirectconceptname] = useState<string>('');
  const [directfilteredData, setDirectfilteredData] = useState(directData);
  const [indirectfilteredData, setIndirectfilteredData] = useState(indirectData);
  const [isDirectBulkUpdating, setIsDirectBulkUpdating] = useState<boolean>(false);
  const [isIndirectBulkUpdating, setIsIndirectBulkUpdating] = useState<boolean>(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState<boolean>(false);

  useEffect(() => {
    // Update filtered data whenever the original data or search term changes.
    // An empty list must also be applied, otherwise the last concept moved out of
    // To-Do after a save would keep showing.
    if (directData) {
      setDirectfilteredData(
        directData.filter(item =>
          item?.Concept?.toLowerCase()?.includes(directConceptname.toLowerCase())
        )
      );
    }

    if (indirectData) {
      setIndirectfilteredData(
        indirectData.filter(item =>
          item?.Concept?.toLowerCase()?.includes(indirectConceptname.toLowerCase())
        )
      );
    }
  }, [directData, indirectData, directConceptname, indirectConceptname]);

  const handleDirectSearchChange = (e: any) => {
    e.preventDefault();
    setDirectconceptname(e.target.value);
  };

  const handleIndirectSearchChange = (e: any) => {
    e.preventDefault();
    setIndirectconceptname(e.target.value);
  };

  const getPlanningPeriod = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  const handleDirectBulkUpdate = async () => {
    try {
      setIsDirectBulkUpdating(true);
      const direct_list = bulkUpdateDirectTodoNDC.flatMap(item =>
        item.NDC11_list.map(ndc => ndc.NDC11)
      );
      await bulkUpdateConstrainedForecast(direct_list, getPlanningPeriod());
      toast.success('Bulk Update Successful');
    } catch (error) {
      console.log('Error while bulk updating=> ', error);
      toast.error('Unable to Bulk Update');
    } finally {
      setIsDirectBulkUpdating(false);
    }
  };

  const handleIndirectBulkUpdate = async () => {
    try {
      setIsIndirectBulkUpdating(true);
      const indirect_list = bulkUpdateIndirectTodoNDC.flatMap(item =>
        item.NDC11_list.map(ndc => ndc.NDC11)
      );
      await bulkUpdateConstrainedForecast(indirect_list, getPlanningPeriod());
      toast.success('Bulk Update Successful');
    } catch (error) {
      console.log('Error while bulk updating=> ', error);
      toast.error('Unable to Bulk Update');
    } finally {
      setIsIndirectBulkUpdating(false);
    }
  };

  const handleBulkUpdate = async () => {
    try {
      setIsBulkUpdating(true);
      const direct_list = bulkUpdateDirectTodoNDC.flatMap(item =>
        item.NDC11_list.map(ndc => ndc.NDC11)
      );
      const indirect_list = bulkUpdateIndirectTodoNDC.flatMap(item =>
        item.NDC11_list.map(ndc => ndc.NDC11)
      );
      await bulkUpdateConstrainedForecast(
        direct_list.concat(indirect_list),
        getPlanningPeriod()
      );
      toast.success('Bulk Update Successful');
    } catch (error) {
      console.log('Error while bulk updating=> ', error);
      toast.error('Unable to Bulk Update');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return {
    bulkUpdateDirectTodoNDC,
    bulkUpdateDirectCompletedNDC,
    bulkUpdateIndirectTodoNDC,
    bulkUpdateIndirectCompletedNDC,
    bulkUpdateNDCs,
    isForecastBulkUpdating,
    directConceptname,
    indirectConceptname,
    directfilteredData,
    indirectfilteredData,
    isDirectBulkUpdating,
    isIndirectBulkUpdating,
    isBulkUpdating,
    setDirectconceptname,
    setIndirectconceptname,
    setDirectfilteredData,
    handleDirectSearchChange,
    handleIndirectSearchChange,
    setIndirectfilteredData,
    handleDirectBulkUpdate,
    handleIndirectBulkUpdate,
    handleBulkUpdate,
  };
};
